'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface DemoStep {
  id: number;
  title: string;
  description: string;
  action: string;
  visual: string;
}

const InteractiveDemo = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const demoSteps: DemoStep[] = [
    {
      id: 1,
      title: "Vehicle Needs Attention",
      description: "A parked vehicle needs to be contacted, and you want to reach the owner without direct confrontation.",
      action: "Locate the ParkPing card placed inside the vehicle",
      visual: "🚗"
    },
    {
      id: 2,
      title: "Scan or Call",
      description: "Use your phone to scan the QR code or call the ParkPing number shown on the card.",
      action: "Your phone opens the ParkPing contact option",
      visual: "📱"
    },
    {
      id: 3,
      title: "Call Routed Securely",
      description: "ParkPing routes the call to the vehicle owner without revealing any personal phone number.",
      action: "Communication happens through ParkPing",
      visual: "📞"
    },
    {
      id: 4,
      title: "Situation Handled",
      description: "The vehicle owner is informed and can respond accordingly. Communication remains respectful and private.",
      action: "Personal details remain protected",
      visual: "✅"
    }
  ];

  // Auto-play logic (optional – you can remove or adjust timing if unwanted)
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % demoSteps.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPlaying, demoSteps.length]);

  const handleNext = () => {
    if (currentStep < demoSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setCurrentStep(0);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block mb-4 px-6 py-2 bg-[#42AF47]/10 rounded-full border border-[#42AF47]/20">
              <span className="text-sm font-semibold text-[#42AF47]">Interactive Walkthrough</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
              See How ParkPing Works
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explore a simple parking scenario to understand how ParkPing enables contact without sharing personal phone numbers.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100">
            <div className="bg-gray-50 rounded-2xl p-8 mb-8">
              <div className="text-center mb-8">
                <div className="text-7xl md:text-8xl mb-6">{demoSteps[currentStep].visual}</div>
                <h3 className="text-2xl md:text-3xl font-heading font-bold text-gray-900 mb-4">
                  {demoSteps[currentStep].title}
                </h3>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  {demoSteps[currentStep].description}
                </p>
                <div className="inline-block px-5 py-2.5 bg-[#42AF47]/10 rounded-lg border border-[#42AF47]/20">
                  <p className="text-[#42AF47] font-medium text-base">
                    {demoSteps[currentStep].action}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-center gap-3 mb-8">
                {demoSteps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentStep(index)}
                    className={`h-3 rounded-full transition-all duration-300 ${
                      index === currentStep
                        ? 'w-10 bg-[#42AF47] shadow-sm shadow-[#42AF47]/30'
                        : 'w-3 bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Go to step ${index + 1}`}
                  />
                ))}
              </div>

              <div className="flex items-center justify-center gap-5">
                <button
                  onClick={handlePrev}
                  disabled={currentStep === 0}
                  className="p-4 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Previous step"
                >
                  <Icon name="ChevronLeftIcon" size={28} className="text-gray-700" />
                </button>

                <button
                  onClick={handlePlayPause}
                  className="px-8 py-4 bg-[#42AF47] hover:brightness-105 hover:shadow-lg rounded-full transition-all duration-300 flex items-center gap-3 shadow-md"
                  aria-label={isPlaying ? 'Pause demo' : 'Play demo'}
                >
                  <Icon name={isPlaying ? 'PauseIcon' : 'PlayIcon'} size={24} className="text-white" />
                  <span className="text-white font-semibold text-base">
                    {isPlaying ? 'Pause' : 'Play'}
                  </span>
                </button>

                <button
                  onClick={handleNext}
                  disabled={currentStep === demoSteps.length - 1}
                  className="p-4 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Next step"
                >
                  <Icon name="ChevronRightIcon" size={28} className="text-gray-700" />
                </button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
              {demoSteps.map((step, index) => (
                <button
                  key={step.id}
                  onClick={() => setCurrentStep(index)}
                  className={`p-6 rounded-2xl transition-all duration-300 text-center border ${
                    index === currentStep
                      ? 'bg-[#42AF47]/10 border-[#42AF47]/30 shadow-md scale-[1.03]'
                      : 'bg-white border-gray-200 hover:border-[#42AF47]/30 hover:bg-[#42AF47]/5'
                  }`}
                >
                  <div className="text-5xl mb-4">{step.visual}</div>
                  <p className="text-base font-semibold text-gray-900">{step.title}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveDemo;